import Analytics from 'analytics-opencare';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Logger from 'utils/Logger';
import { useAnalyticsService } from 'services/AnalyticsService';

import { UserMarketName } from 'connect/saga/handlers/user/user-market-name';
import { ForcedExperimentVariantsStateHandler } from 'connect/state/handlers/experiments';

import { GetExperimentVariants } from '../../connect/saga/handlers/experiment/get-experiment-variants';
import { VARIANTS } from '../constants/experiment-constants';

const analytics = new Analytics();

export const useEffectIf = ({ effect, watchList, condition }) => {
  useEffect(() => {
    const doesConditionMatch =
      typeof condition === 'function' ? condition() : condition;
    if (!doesConditionMatch) {
      return;
    }

    const cleanup = effect();
    if (typeof cleanup === 'function') {
       
      return cleanup;
    }
  }, watchList);
};

export const usePrevious = value => {
  const ref = useRef();

  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

const findExperimentVariant = (experimentVariants, experimentName) => {
  const matchingExperiments = experimentVariants.filter(
    variant => variant.name === experimentName,
  );

  if (matchingExperiments.length === 0) {
    return null;
  }

  if (matchingExperiments.length > 1) {
    Logger.error(
      `More than one variant found for experiment ${experimentName}`,
    );
  }

  return matchingExperiments[0].variant.variantNumber;
};

export const useExperiment = ({ experiment, condition, skipTracking }) => {
  const analyticsService = useAnalyticsService();
  const userMarket = useSelector(UserMarketName.value.selector);
  const experimentVariants = useSelector(GetExperimentVariants.value.selector);
  const forcedExperimentVariants = useSelector(
    ForcedExperimentVariantsStateHandler.selector,
  );

  // Shouldn't ever happen since this is loaded in App/index.js, but just in case
  if (!experimentVariants?.length) {
    Logger.error('No experimentVariants exist in Redux');
  }

  const createResult = ({ variantNumber }) => {
    const type = variantNumber === '0' ? VARIANTS.CONTROL : VARIANTS.VARIANT;
    return {
      variant: variantNumber,
      isControl: type === VARIANTS.CONTROL,
      isVariant: type === VARIANTS.VARIANT,
    };
  };

  const [result, setResult] = useState(null);

  // Check if the experiment is applicable to the current user
  const doesConditionPass = condition || condition === undefined;
  const doesMarketMatch =
    !experiment.markets
    || experiment.markets.includes(userMarket?.toLowerCase());
  const isExperimentApplicable = doesConditionPass && doesMarketMatch;

  useEffect(() => {
    if (!isExperimentApplicable || !experimentVariants?.length) {
      return;
    }

    const variant =
      forcedExperimentVariants?.[experiment.name]
      || findExperimentVariant(experimentVariants, experiment.name);

    if (!variant) {
      Logger.error(`No variant found for experiment ${experiment.name}`);
      return;
    }

    setResult(
      createResult({
        variantNumber: variant,
      }),
    );
  }, [isExperimentApplicable]);

  // Track in heap when the experiment variant is assigned
  useEffect(() => {
    if (!result || skipTracking) {
      return;
    }

    analytics.trackPageView({
      pageType: analytics.pageTypes.EXPERIMENT_VARIANT_ASSIGNED,
      content: {
        no_track_cro: 'true',
        experiment_name: experiment.name,
        experiment_variant: result.variant,
      },
      ...analyticsService.analyticsData,
    });
  }, [result, skipTracking]);

  return result;
};
